/**
 * AvatarFigure v2 — expedition-journal traveler in stylized-cartoon style.
 *
 * Goals over v1:
 *   - Real anatomy (7-head proportions, shoulder curves, neck depth)
 *   - Layered lighting: ambient fill + rim light + cast shadow beneath
 *   - Actual face: subtle nose, mouth curve, eyebrows over eye whites
 *   - Cleaner silhouettes for hair (long, curly, layered, braids, pixie)
 *   - More clothing pieces + neckwear + facial hair + eyewear
 *
 * This is still hand-coded SVG (not commissioned art) so it's honest about
 * being a design system, not a photorealistic renderer. But the fidelity
 * gap vs. v1 is deliberately large — v1 looked like clip-art; v2 should
 * read as intentional illustration.
 */
import React from 'react';

// ---------- Palettes ----------
const SKIN = {
  porcelain: { fill: '#F5D9BF', shade: '#E8B892', warm: '#F7E1CC' },
  fair:      { fill: '#EFC49B', shade: '#D5A276', warm: '#F1CFAA' },
  tan:       { fill: '#D8A374', shade: '#B7834E', warm: '#DDAF87' },
  bronze:    { fill: '#B47B4E', shade: '#8E5A31', warm: '#BB8A62' },
  deep:      { fill: '#8A5A34', shade: '#663F1F', warm: '#9A6B44' },
  espresso:  { fill: '#5C3A22', shade: '#3F2413', warm: '#6D4B31' },
  glacier_sheen:    { fill: '#CFE3E6', shade: '#A7C4CB', warm: '#DDEDEF' },
  trailmarked_gold: { fill: '#CBA135', shade: '#8F6E1A', warm: '#DBB756' },
};
const HAIR = {
  hc_black:  '#231A15', hc_brown: '#5B3A20', hc_auburn: '#8B3A1F',
  hc_blonde: '#D4A857', hc_silver:'#C2C6C9', hc_rose:   '#D69EA0', hc_blue:  '#3E5B94',
};
const EYE = {
  ey_brown: '#5B3A20', ey_hazel: '#8A6A3A', ey_green: '#4E7A55',
  ey_blue:  '#3E7484', ey_grey:  '#7C8590', ey_amber: '#CBA135',
};
const OUTFIT = {
  tee_shorts:  { top: '#7c8471', bottom: '#3d4a3a' },
  layers:      { top: '#5B6E52', bottom: '#3d4a3a' },
  rain_shell:  { top: '#F0C05A', bottom: '#4b463c' },
  tweed:       { top: '#7d6446', bottom: '#4b463c' },
  summit_coat: { top: '#3E7484', bottom: '#2E3540' },
  denim_jkt:   { top: '#3E5B94', bottom: '#4b463c' },
  hoodie:      { top: '#B33A2E', bottom: '#4b463c' },
  linen_dress: { top: '#E8D5A8', bottom: '#E8D5A8' },
};
const FOOTWEAR = {
  sneakers: '#e8e2d6', trail_low: '#3d4a3a', boots_tan: '#8a5a34',
  boots_pro: '#4b463c', firewalker: '#C1502E',
};
const HEADWEAR = { cap: '#B33A2E', beret: '#8E2F44', sunhat: '#CBA135', pith: '#8B7D5D', beanie: '#3E7484' };
const BACKPACK = { sling: '#4b463c', daypack: '#3d4a3a', overnight: '#5B3A20', continental: '#C1502E' };
const BACKDROP = { parchment: '#DCD3BD', meadow: '#7c9a63', harbor: '#3E7484', sunset: '#C1502E', aurora: '#5B4A94' };

// ---------- Hair (long, curly, straight, braids, buns…) ----------
const Hair = ({ id, color }) => {
  const c = color || '#231A15';
  const dark = shade(c, 0.75);
  switch (id) {
    case 'bald': return null;
    case 'crop':
      return <path d="M64,66 q0,-30 37,-30 q37,0 37,30 q-4,-8 -10,-10 q-6,-14 -27,-14 q-21,0 -27,14 q-6,2 -10,10 z" fill={c} />;
    case 'waves':
      return (
        <>
          <path d="M62,72 q4,-30 39,-30 q35,0 39,30 q-2,-12 -10,-18 q-8,-16 -29,-16 q-21,0 -29,16 q-8,6 -10,18 z" fill={c} />
          <path d="M70,68 q6,-4 12,0 M92,58 q6,-4 12,0 M110,62 q6,-4 12,0 M124,68 q4,-2 8,0" stroke={dark} strokeWidth="1.4" fill="none" opacity=".6" strokeLinecap="round" />
        </>
      );
    case 'curls':
      return (
        <g>
          <circle cx="78" cy="60" r="10" fill={c} /><circle cx="90" cy="48" r="11" fill={c} />
          <circle cx="106" cy="46" r="11" fill={c} /><circle cx="122" cy="54" r="10" fill={c} />
          <circle cx="128" cy="70" r="8" fill={c} /><circle cx="72" cy="72" r="8" fill={c} />
          <circle cx="86" cy="60" r="4" fill={dark} opacity=".35" />
          <circle cx="112" cy="54" r="4" fill={dark} opacity=".35" />
        </g>
      );
    case 'ponytail':
      return (
        <>
          <path d="M64,66 q0,-30 37,-30 q37,0 37,30 q-4,-8 -10,-10 q-6,-14 -27,-14 q-21,0 -27,14 q-6,2 -10,10 z" fill={c} />
          <path d="M132,68 q18,10 14,42 q-3,14 -16,16 q10,-10 12,-30 q0,-16 -10,-28 z" fill={c} />
          <path d="M136,74 q10,10 8,28" stroke={dark} strokeWidth="1.5" fill="none" opacity=".5" />
        </>
      );
    case 'bun':
      return (
        <>
          <path d="M66,64 q0,-28 35,-28 q35,0 35,28 q-4,-6 -10,-8 q-6,-14 -25,-14 q-19,0 -25,14 q-6,2 -10,8 z" fill={c} />
          <circle cx="101" cy="28" r="14" fill={c} />
          <ellipse cx="101" cy="26" rx="10" ry="4" fill={dark} opacity=".35" />
        </>
      );
    case 'afro':
      return (
        <>
          <circle cx="101" cy="50" r="38" fill={c} />
          <circle cx="76" cy="64" r="12" fill={c} /><circle cx="126" cy="64" r="12" fill={c} />
          <circle cx="88" cy="42" r="8" fill={dark} opacity=".3" />
          <circle cx="112" cy="42" r="8" fill={dark} opacity=".3" />
        </>
      );
    case 'mohawk':
      return (
        <>
          <path d="M78,68 a24,24 0 0 1 46,0 q-4,-2 -8,-2 q-8,-6 -15,-6 q-7,0 -15,6 q-4,0 -8,2 z" fill={c} opacity=".6" />
          <path d="M94,22 q7,-3 14,0 v50 h-14 z" fill={c} />
          <path d="M96,24 q6,-2 10,0" stroke={dark} strokeWidth="1.5" fill="none" opacity=".7" />
        </>
      );
    case 'long_braid':
      return (
        <>
          <path d="M64,66 q0,-30 37,-30 q37,0 37,30 q-4,-8 -10,-10 q-6,-14 -27,-14 q-21,0 -27,14 q-6,2 -10,10 z" fill={c} />
          <path d="M101,94 q-8,10 -4,18 q-8,10 -4,18 q-8,10 -4,18 q-6,10 0,16" stroke={c} strokeWidth="10" fill="none" strokeLinecap="round" />
          <path d="M101,94 q-8,10 -4,18 q-8,10 -4,18 q-8,10 -4,18 q-6,10 0,16" stroke={dark} strokeWidth="1.5" fill="none" opacity=".4" />
        </>
      );
    case 'pixie':
      return (
        <>
          <path d="M70,62 q0,-24 31,-24 q31,0 31,24 q-2,-6 -8,-6 q0,-10 -23,-12 q-23,2 -23,12 q-6,0 -8,6 z" fill={c} />
          <path d="M74,60 q10,-4 22,0 q10,-4 22,0" stroke={dark} strokeWidth="1.4" fill="none" opacity=".5" />
        </>
      );
    case 'layered':
      return (
        <>
          <path d="M62,74 q0,-34 39,-34 q39,0 39,34 q-4,-14 -12,-20 q-8,-18 -27,-18 q-19,0 -27,18 q-8,6 -12,20 z" fill={c} />
          <path d="M70,74 q30,-8 60,0" stroke={dark} strokeWidth="1.5" fill="none" opacity=".45" />
          <path d="M76,84 q26,-6 50,0" stroke={dark} strokeWidth="1.3" fill="none" opacity=".35" />
        </>
      );
    case 'braids':
      return (
        <>
          <path d="M66,64 q0,-28 35,-28 q35,0 35,28 q-4,-6 -10,-8 q-6,-14 -25,-14 q-19,0 -25,14 q-6,2 -10,8 z" fill={c} />
          <path d="M78,96 q-4,14 -2,28 q-4,10 0,20" stroke={c} strokeWidth="7" fill="none" strokeLinecap="round" />
          <path d="M124,96 q4,14 2,28 q4,10 0,20" stroke={c} strokeWidth="7" fill="none" strokeLinecap="round" />
        </>
      );
    default:
      return <path d="M64,66 q0,-30 37,-30 q37,0 37,30 q-4,-8 -10,-10 q-6,-14 -27,-14 q-21,0 -27,14 q-6,2 -10,10 z" fill={c} />;
  }
};

// ---------- Facial hair ----------
const FacialHair = ({ id, color }) => {
  const c = color || '#231A15';
  switch (id) {
    case 'stubble':
      return <ellipse cx="101" cy="90" rx="16" ry="5" fill={c} opacity=".22" />;
    case 'goatee':
      return <path d="M95,88 q6,10 12,0 q0,10 -6,14 q-6,-4 -6,-14 z" fill={c} />;
    case 'mustache':
      return <path d="M89,84 q6,-3 12,0 q6,-3 12,0 q-2,4 -6,4 q-6,-2 -12,0 q-4,0 -6,-4 z" fill={c} />;
    case 'full_beard':
      return (
        <>
          <path d="M78,80 q0,20 23,26 q23,-6 23,-26 q-8,8 -23,8 q-15,0 -23,-8 z" fill={c} />
          <path d="M89,84 q6,-3 12,0 q6,-3 12,0 q-2,4 -6,4 q-6,-2 -12,0 q-4,0 -6,-4 z" fill={c} />
        </>
      );
    default: return null;
  }
};

// ---------- Eyewear ----------
const Eyewear = ({ id }) => {
  switch (id) {
    case 'round_glasses':
      return (
        <>
          <circle cx="91" cy="73" r="7" fill="none" stroke="#1a1512" strokeWidth="1.6" />
          <circle cx="111" cy="73" r="7" fill="none" stroke="#1a1512" strokeWidth="1.6" />
          <line x1="98" y1="73" x2="104" y2="73" stroke="#1a1512" strokeWidth="1.6" />
        </>
      );
    case 'square_glasses':
      return (
        <>
          <rect x="84" y="68" width="14" height="10" rx="2" fill="none" stroke="#1a1512" strokeWidth="1.6" />
          <rect x="104" y="68" width="14" height="10" rx="2" fill="none" stroke="#1a1512" strokeWidth="1.6" />
          <line x1="98" y1="73" x2="104" y2="73" stroke="#1a1512" strokeWidth="1.6" />
        </>
      );
    case 'aviators':
      return (
        <>
          <path d="M84,70 q0,-3 4,-3 h10 q4,0 4,3 v6 q0,4 -6,5 q-6,-1 -8,-5 z" fill="#3E5B94" opacity=".6" stroke="#1a1512" strokeWidth="1.4" />
          <path d="M104,70 q0,-3 4,-3 h10 q4,0 4,3 v6 q0,4 -6,5 q-6,-1 -8,-5 z" fill="#3E5B94" opacity=".6" stroke="#1a1512" strokeWidth="1.4" />
          <line x1="98" y1="73" x2="104" y2="73" stroke="#1a1512" strokeWidth="1.4" />
        </>
      );
    default: return null;
  }
};

// ---------- Neckwear ----------
const Neckwear = ({ id }) => {
  switch (id) {
    case 'scarf':
      return (
        <>
          <path d="M84,100 q17,-4 34,0 q0,10 -3,14 q-6,-2 -14,-2 q-8,0 -14,2 q-3,-4 -3,-14 z" fill="#C1502E" />
          <path d="M112,112 l4,20 l-8,-4 z" fill="#C1502E" />
          <path d="M84,104 q17,4 34,0" stroke="#8B2F1E" strokeWidth="1" fill="none" opacity=".5" />
        </>
      );
    case 'bandana':
      return <path d="M84,100 q17,-6 34,0 q0,4 -3,6 q-14,-4 -28,0 q-3,-2 -3,-6 z" fill="#B33A2E" />;
    default: return null;
  }
};

// ---------- Headwear ----------
const Headwear = ({ id, color }) => {
  const c = color || HEADWEAR[id] || '#B33A2E';
  const dark = shade(c, 0.7);
  switch (id) {
    case 'none_hat': return null;
    case 'cap':
      return (
        <>
          <path d="M70,50 q6,-16 31,-16 q25,0 31,16 v6 h-62 z" fill={c} />
          <path d="M132,50 q14,0 18,6 v3 q0,3 -4,3 h-14 z" fill={c} />
          <path d="M70,52 q31,-8 62,0" stroke={dark} strokeWidth="1" fill="none" opacity=".5" />
        </>
      );
    case 'beret':
      return (
        <>
          <ellipse cx="101" cy="40" rx="34" ry="14" fill={c} />
          <ellipse cx="101" cy="46" rx="30" ry="6" fill={dark} opacity=".4" />
          <circle cx="120" cy="34" r="3.5" fill={c} />
        </>
      );
    case 'sunhat':
      return (
        <>
          <ellipse cx="101" cy="50" rx="54" ry="7" fill={c} />
          <path d="M74,50 q6,-20 27,-20 q21,0 27,20 z" fill={c} />
          <ellipse cx="101" cy="50" rx="54" ry="7" fill={dark} opacity=".35" />
          <rect x="74" y="46" width="54" height="3" fill={dark} opacity=".4" />
        </>
      );
    case 'pith':
      return (
        <>
          <ellipse cx="101" cy="52" rx="46" ry="7" fill={c} />
          <path d="M74,52 q5,-24 27,-24 q22,0 27,24 z" fill={c} />
          <rect x="74" y="48" width="54" height="4" fill={dark} opacity=".55" />
        </>
      );
    case 'beanie':
      return (
        <>
          <path d="M70,50 q0,-24 31,-24 q31,0 31,24 v6 h-62 z" fill={c} />
          <path d="M70,54 q31,-6 62,0" stroke={dark} strokeWidth="2" fill="none" opacity=".55" />
          <path d="M70,60 q31,-6 62,0" stroke={dark} strokeWidth="2" fill="none" opacity=".4" />
        </>
      );
    default: return null;
  }
};

// ---------- Backpack ----------
const BackpackLayer = ({ id, color }) => {
  const c = color || BACKPACK[id] || '#4b463c';
  const dark = shade(c, 0.7);
  if (id === 'none_pack' || !id) return null;
  return (
    <g>
      <rect x="76" y="98" width="7" height="30" rx="3" fill={c} />
      <rect x="119" y="98" width="7" height="30" rx="3" fill={c} />
      {(id === 'daypack' || id === 'overnight' || id === 'continental') && (
        <g>
          <rect x={id === 'continental' ? 132 : 130} y="108" width={id === 'continental' ? 26 : 20} height={id === 'continental' ? 44 : 36} rx="6" fill={c} />
          <rect x={id === 'continental' ? 138 : 134} y="116" width={id === 'continental' ? 14 : 12} height="4" rx="2" fill={dark} />
          <rect x={id === 'continental' ? 138 : 134} y="128" width={id === 'continental' ? 14 : 12} height="3" rx="1" fill={dark} opacity=".7" />
        </g>
      )}
      {id === 'sling' && <rect x="128" y="118" width="14" height="20" rx="4" fill={c} />}
    </g>
  );
};

// ---------- Companion ----------
const Companion = ({ id }) => {
  switch (id) {
    case 'trail_dog':
      return (
        <g>
          <ellipse cx="180" cy="210" rx="12" ry="5" fill="#1a1512" opacity=".18" />
          <ellipse cx="180" cy="200" rx="20" ry="10" fill="#8a5a34" />
          <circle cx="195" cy="192" r="9" fill="#8a5a34" />
          <path d="M201 187 l-3 -8 l6 5 z M188 187 l-3 -6 l5 3 z" fill="#8a5a34" />
          <circle cx="199" cy="193" r="1.6" fill="#1a1512" />
          <ellipse cx="203" cy="196" rx="2" ry="1.3" fill="#1a1512" opacity=".6" />
          <line x1="165" y1="196" x2="155" y2="192" stroke="#8a5a34" strokeWidth="3" strokeLinecap="round" />
        </g>
      );
    case 'trail_fox':
      return (
        <g>
          <ellipse cx="180" cy="210" rx="12" ry="4" fill="#1a1512" opacity=".18" />
          <ellipse cx="180" cy="200" rx="20" ry="8" fill="#C1502E" />
          <circle cx="195" cy="193" r="8" fill="#C1502E" />
          <path d="M199 187 l0 -8 l6 6 z M191 187 l0 -8 l-6 6 z" fill="#C1502E" />
          <circle cx="199" cy="193" r="1.4" fill="#1a1512" />
          <ellipse cx="203" cy="196" rx="2" ry="1" fill="#F4EEDF" />
          <path d="M162 200 q-10 -4 -14 -12 q2 8 6 12" fill="#C1502E" />
        </g>
      );
    default: return null;
  }
};

// ---------- Footwear ----------
const Footwear = ({ id, color }) => {
  const c = color || FOOTWEAR[id] || '#4b463c';
  const dark = shade(c, 0.65);
  const boot = (x) => (
    <g>
      <path d={`M${x},190 q0,-6 6,-8 h18 q6,0 6,8 v10 q0,4 -4,4 h-22 q-6,0 -6,-4 z`} fill={c} />
      <path d={`M${x + 4},194 l6,-4 M${x + 12},194 l6,-4 M${x + 20},194 l4,-4`} stroke="#F4EEDF" strokeWidth="0.9" opacity=".55" />
      <path d={`M${x},202 h30`} stroke={dark} strokeWidth="1.5" opacity=".8" />
    </g>
  );
  return <>{boot(74)}{boot(97)}</>;
};

// ---------- helper ----------
function shade(hex, amt) {
  // Darken a hex color by mixing toward black. amt in 0..1 (1 = no change).
  try {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const mix = (v) => Math.round(v * amt);
    return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
  } catch (_) { return hex; }
}

// ---------- Main figure ----------
export const AvatarFigure = ({ config = {}, size = 232, floating = true }) => {
  const skin = SKIN[config.skin] || SKIN.porcelain;
  const hairC = HAIR[config.haircolor] || HAIR.hc_black;
  const eyeC = EYE[config.eyecolor] || EYE.ey_brown;
  const outfit = OUTFIT[config.outfit] || OUTFIT.tee_shorts;
  const outfitTop = outfit.top; const outfitBot = outfit.bottom;
  const outfitShade = shade(outfitTop, 0.78);
  const backdrop = BACKDROP[config.backdrop] || BACKDROP.parchment;
  const skinShade = skin.shade;

  return (
    <svg viewBox="0 0 200 232" xmlns="http://www.w3.org/2000/svg" style={{ width: size, height: (size * 232) / 200, display: 'block' }}>
      <defs>
        <radialGradient id="atmo" cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity=".5" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="gShadow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1c1712" stopOpacity=".4" />
          <stop offset="100%" stopColor="#1c1712" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`skinLight-${config.skin || 'p'}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={skin.warm} />
          <stop offset="55%" stopColor={skin.fill} />
          <stop offset="100%" stopColor={skinShade} />
        </linearGradient>
        <linearGradient id={`outfitLight-${config.outfit || 'o'}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={outfitTop} />
          <stop offset="100%" stopColor={outfitShade} />
        </linearGradient>
      </defs>

      {/* Backdrop atmosphere */}
      <rect x="0" y="0" width="200" height="232" rx="4" fill={backdrop} opacity=".55" />
      <ellipse cx="101" cy="60" rx="80" ry="65" fill="url(#atmo)" />

      {/* Companion (behind) */}
      <Companion id={config.companion} />

      <g className={floating ? 'audiostride-figure-float' : ''}>
        {/* Backpack straps behind body */}
        <BackpackLayer id={config.backpack} />

        {/* Legs — separated with a subtle knee suggestion */}
        <path d="M84,148 q-2,26 -2,50 q0,4 4,4 h8 q4,0 4,-4 q-2,-24 0,-50 z" fill={outfitBot} />
        <path d="M116,148 q2,26 2,50 q0,4 -4,4 h-8 q-4,0 -4,-4 q2,-24 0,-50 z" fill={outfitBot} />
        <path d="M88,180 q4,-2 8,0 M112,180 q-4,-2 -8,0" stroke={shade(outfitBot, 0.75)} strokeWidth="1" fill="none" opacity=".6" />

        {/* Footwear */}
        <Footwear id={config.footwear} />

        {/* Torso — sloped shoulders, tapered waist */}
        <path d="M70,102 q0,-8 8,-10 q4,-4 23,-4 q19,0 23,4 q8,2 8,10 v46 q0,6 -6,6 h-50 q-6,0 -6,-6 z" fill={`url(#outfitLight-${config.outfit || 'o'})`} />
        {/* Arms */}
        <path d="M58,108 q0,-6 5,-6 q7,0 7,6 v40 q0,4 -4,4 q-8,0 -8,-6 z" fill={`url(#outfitLight-${config.outfit || 'o'})`} />
        <path d="M144,108 q0,-6 -5,-6 q-7,0 -7,6 v40 q0,4 4,4 q8,0 8,-6 z" fill={`url(#outfitLight-${config.outfit || 'o'})`} />
        {/* Hand tips (skin) */}
        <ellipse cx="62" cy="152" rx="6" ry="5" fill={skin.fill} />
        <ellipse cx="140" cy="152" rx="6" ry="5" fill={skin.fill} />
        {/* Shirt highlight */}
        <path d="M72,105 q5,-4 10,-4 M120,101 q5,0 8,4" stroke={shade(outfitTop, 1.05)} strokeWidth="1" fill="none" opacity=".5" />
        {/* Collar shadow */}
        <path d="M88,98 q13,10 26,0" fill="none" stroke={outfitShade} strokeWidth="2" opacity=".8" />

        {/* Neckwear (over torso, under head) */}
        <Neckwear id={config.neckwear} />

        {/* Neck */}
        <rect x="93" y="88" width="16" height="14" rx="4" fill={skinShade} />
        <path d="M93,90 h16" stroke={skin.warm} strokeWidth="1" opacity=".5" />

        {/* Head */}
        <ellipse cx="101" cy="70" rx="30" ry="32" fill={`url(#skinLight-${config.skin || 'p'})`} />
        {/* Cheek warmth */}
        <ellipse cx="86" cy="82" rx="6" ry="4" fill={skin.warm} opacity=".7" />
        <ellipse cx="116" cy="82" rx="6" ry="4" fill={skin.warm} opacity=".7" />
        {/* Chin shadow */}
        <ellipse cx="101" cy="94" rx="12" ry="4" fill={skinShade} opacity=".35" />

        {/* Hair (behind headwear) */}
        <Hair id={config.hairstyle} color={hairC} />

        {/* Eyebrows */}
        <path d="M84,66 q6,-3 12,0" stroke={hairC} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <path d="M106,66 q6,-3 12,0" stroke={hairC} strokeWidth="2.2" fill="none" strokeLinecap="round" />

        {/* Eyes — white + iris + pupil + highlight */}
        <ellipse cx="91" cy="73" rx="4" ry="3" fill="#fdfaf3" />
        <ellipse cx="111" cy="73" rx="4" ry="3" fill="#fdfaf3" />
        <circle cx="91" cy="73" r="2.4" fill={eyeC} />
        <circle cx="111" cy="73" r="2.4" fill={eyeC} />
        <circle cx="91" cy="73" r="1" fill="#1a1512" />
        <circle cx="111" cy="73" r="1" fill="#1a1512" />
        <circle cx="90" cy="72" r="0.6" fill="#fff" />
        <circle cx="110" cy="72" r="0.6" fill="#fff" />

        {/* Nose bridge shadow */}
        <path d="M101,77 q-2,5 -3,8 q3,2 6,0 q-1,-3 -3,-8" fill={skinShade} opacity=".35" />
        {/* Mouth — subtle smile */}
        <path d="M92,88 q9,6 18,0" stroke={shade(skin.fill, 0.6)} strokeWidth="1.8" fill="none" strokeLinecap="round" />
        <path d="M96,89 q5,3 10,0" stroke="#C1502E" strokeWidth="1.2" fill="none" opacity=".6" />

        {/* Facial hair */}
        <FacialHair id={config.facialhair} color={hairC} />
        {/* Eyewear on top */}
        <Eyewear id={config.eyewear} />
        {/* Headwear on top of everything on head */}
        <Headwear id={config.headwear} />
      </g>

      {/* Cast shadow beneath the figure */}
      <ellipse cx="101" cy="216" rx="46" ry="6" fill="url(#gShadow)" />
    </svg>
  );
};

export default AvatarFigure;
